import CloseRounded from '@mui/icons-material/CloseRounded';
import { Alert, Box, Button, Card, Modal, Stack, Typography, Table, TableBody, TableCell, TableHead, TableRow, Chip } from '@mui/material';
import AdditionalWorkshopCartContext from 'contexts/AdditionalWorkshopCartContext';
import useBoolean from 'hooks/useBoolean';
import ChevronLeft from 'icons/ChevronLeft';
import ChevronRight from 'icons/ChevronRight';
import DashboardLayout from 'layouts/Dashboard/DashboardLayout';
import { useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axiosInstance from 'utils/axios';
import WorkshopCard from '../ChooseWorkshop/partials/WorkshopCard';
import formatCurrency from 'utils/formatCurrency';
import FormatDate from 'utils/formatDate';

const ChooseAdditionalWorkshop = () => {
    const [dataWorkshop, setDataWorkshop] = useState([]);
    const [messageError, setMessageError] = useState();
    const [eligible, setEligible] = useState(false);
    const [loading, setLoading] = useState(true);
    const cart = useContext(AdditionalWorkshopCartContext);
    const openModal = useBoolean();
    const navigate = useNavigate();

    useEffect(() => {
        const getData = async () => {
            try {
                const response = await axiosInstance.get('/api/prv/member-invoices', {
                    params: { 
                        packageTypes: ['main', 'additional'],
                        statuses: ['paid', 'pending'],
                    },
                });

                await handleResponse(response.data);
            } catch (_) {
                setEligible(false);
            }

            setLoading(false);
        };
        getData();
    }, []);

    const handleResponse = async (invoices) => {
        const pendingInvoice = invoices.find((i) => i.status?.value === 'pending');
        const paidInvoices = invoices.filter((i) => i.status?.value === 'paid');

        if (pendingInvoice) {
            handlePendingInvoice(pendingInvoice);

            return;
        }

        if (paidInvoices.length) {
            setEligible(true);
            const paidWorkshops = paidInvoices
                .flatMap((inv) => inv?.workshops || [])
                .map((workshop) => workshop)
                .sort((a, b) => a.order - b.order);

            cart.setPaidWorkshops(paidWorkshops);

            await getWorkshopList(paidWorkshops);
        } else {
            setEligible(false);
        }
    };

    const getWorkshopList = async () => {
        try {
            const response = await axiosInstance.get('/api/prv/workshops');
            setDataWorkshop(response.data || []);
        } catch (_) {}
    };

    const handlePendingInvoice = (pendingInvoice) => {
        if (pendingInvoice?.package?.type === 'main') {
            
            return;
        }
        cart.setFromInvoice(pendingInvoice);
        navigate('/dashboard/payment-complete-additional');
    };

    const isConflict = (data) => {
        if (alreadyChooseUnpaid(data)) return false;

        return cart.workshops.some((w) => {
            const a = w.workshop?.timeRange || [];
            const b = data?.timeRange || [];
            return a.some((slot) => b.includes(slot));
        });
    };

    const alreadyChooseUnpaid = (data) => {
        return cart.workshops.some(
            (w) => !w.isPaid && w.workshop?.id === data.id,
        );
    };

    const existsAsPaid = (data) => {
        return cart.workshops.some(
            (w) => w.isPaid && w.workshop?.id === data.id,
        );
    };

    const isValidToAdd = (data) => {
        if (existsAsPaid(data)) {
            openModal.onTrue();
            setMessageError('You already own this workshop');
            return false;
        }

        if (isConflict(data)) {
            openModal.onTrue();
            setMessageError('The workshop you selected conflicts with other workshops');
            return false;
        }

        return true;
    };

    const chooseWorkshop = (data) => {
        if (!isValidToAdd(data)) return;

        alreadyChooseUnpaid(data) ? cart.removeWorkshop(data) : cart.addWorkshop(data);
    };

    const hasUnpaid = useMemo(
        () => cart.workshops.some((w) => !w.isPaid),
        [cart.workshops],
    );

    const handleNext = () => {
        navigate('/dashboard/additional-payment');
    };

    const totalUnpaid = useMemo(
        () =>
            (cart.workshops || []).reduce((acc, cur) => {
                if (cur?.isPaid) return acc;
                const price = parseInt(cur?.workshop?.price || 0);
                return acc + (isNaN(price) ? 0 : price);
            }, 0),
        [cart.workshops],
    );

    if (!loading && !eligible) {
        return (
            <DashboardLayout>
                <Box pt={2} pb={4}>
                    <Card sx={{ p: 4 }}>
                        <Stack spacing={2} sx={{ alignItems: 'center' }}>
                            <Alert severity="warning" sx={{ width: '100%' }}>
                                You must have a paid symposium before purchasing any additional workshops.
                            </Alert>
                            <Button
                                variant="contained"
                                onClick={() => navigate('/dashboard/choose-package')}
                            >
                                Go to Choose Package
                            </Button>
                        </Stack>
                    </Card>
                </Box>
            </DashboardLayout>
        );
    }

    return (
        <DashboardLayout>
            <Modal
                open={openModal.value}
                onClose={() => openModal.onFalse()}
                aria-labelledby="modal-modal-title"
                aria-describedby="modal-modal-description"
            >
                <Box
                    sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        width: { xs: '85%', sm: 420 },
                        bgcolor: 'background.paper',
                        borderRadius: 3,
                        boxShadow: '0 24px 60px rgba(0,0,0,0.25)',
                        p: 4,
                        textAlign: 'center',
                    }}
                >
                    <Box
                        sx={{
                            width: 84,
                            height: 84,
                            borderRadius: '50%',
                            bgcolor: 'error.main',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            mx: 'auto',
                            mb: 2,
                            boxShadow: '0 12px 24px rgba(255,0,0,0.35)',
                        }}
                    >
                        <CloseRounded sx={{ fontSize: 56, color: 'common.white' }} />
                    </Box>
                    <Typography id="modal-modal-title" variant="h5" fontWeight={800}>
                        Failed to Add Workshop
                    </Typography>
                    <Typography
                        id="modal-modal-description"
                        sx={{ mt: 1.5 }}
                        color="text.secondary"
                    >
                        {messageError ??
                            'We were unable to process your request. Please check your selections and try again.'}
                    </Typography>
                    <Button
                        onClick={() => openModal.onFalse()}
                        variant="contained"
                        sx={{
                            mt: 3,
                            borderRadius: 2,
                            px: 4,
                            backgroundColor: 'grey.300',
                            color: 'text.primary',
                            '&:hover': { backgroundColor: 'grey.400' },
                        }}
                    >
                        CLOSE
                    </Button>
                </Box>
            </Modal>

            <Box pt={2} pb={4}>
                <Card sx={{ p: 4 }}>
                    <Box
                        mb={2}
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                        }}
                    >
                        <Button
                            disableRipple
                            size="small"
                            variant="contained"
                            startIcon={<ChevronLeft />}
                            onClick={() => navigate('/dashboard')}
                        >
                            Back
                        </Button>
                        <Typography variant="h5" fontWeight={600}>
                            Choose Additional Workshop
                        </Typography>
                        <Button
                            disableRipple
                            variant="contained"
                            endIcon={<ChevronRight />}
                            onClick={() => handleNext()}
                            disabled={!hasUnpaid}
                        >
                            Next
                        </Button>
                    </Box>

                    {cart.workshops?.length > 0 && (
                        <Box sx={{ mb: 3, overflowX: 'auto' }}>
                            <Table size="small" sx={{ minWidth: 560 }}>
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 700 }}>No</TableCell>
                                        <TableCell sx={{ fontWeight: 700 }}>Workshop</TableCell>
                                        <TableCell sx={{ fontWeight: 700 }}>Date & Time</TableCell>
                                        <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 700 }}>
                                            Price (Unpaid)
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {cart.workshops.map((w, idx) => {
                                        const isPaid = !!w?.isPaid;
                                        const price = parseInt(w?.workshop?.price || 0);
                                        const dateText = w?.workshop?.date ? FormatDate(w.workshop.date, 'dd MMMM yyyy') : '-';
                                        const timeText = w?.workshop?.time || '-';
                                        return (
                                            <TableRow key={idx}>
                                                <TableCell>{idx + 1}</TableCell>
                                                <TableCell>{w?.workshop?.name || '-'}</TableCell>
                                                <TableCell>
                                                    <Stack spacing={0.25}>
                                                        <Typography variant="body2">{dateText}</Typography>
                                                        <Typography variant="body2" color="text.secondary">
                                                            {timeText}
                                                        </Typography>
                                                    </Stack>
                                                </TableCell>
                                                <TableCell>
                                                    <Chip
                                                        size="small"
                                                        label={isPaid ? 'Paid' : 'Unpaid'}
                                                        color={isPaid ? 'success' : 'warning'}
                                                        variant="outlined"
                                                    />
                                                </TableCell>
                                                <TableCell align="right">
                                                    {isPaid ? '-' : formatCurrency(isNaN(price) ? 0 : price)}
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                    <TableRow>
                                        <TableCell />
                                        <TableCell />
                                        <TableCell />
                                        <TableCell sx={{ fontWeight: 700 }}>Total</TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 700 }}>
                                            {formatCurrency(totalUnpaid)}
                                        </TableCell>
                                    </TableRow>
                                </TableBody>
                            </Table>
                        </Box>
                    )}

                    <Box
                        sx={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: 3,
                            justifyContent: 'center',
                            mb: 4,
                        }}
                    >
                        {dataWorkshop
                            ?.sort((a, b) => a.order - b.order)
                            .map((item, index) => (
                                <Box
                                    key={index}
                                    sx={{
                                        flex: '1 1 340px',
                                        maxWidth: 360,
                                    }}
                                >
                                    <WorkshopCard
                                        item={item}
                                        selected={cart.workshops.some(
                                            (w) => !w.isPaid && w.workshop?.id === item.id,
                                        )}
                                        isPaid={cart.workshops.some(
                                            (w) => w.isPaid && w.workshop?.id === item.id,
                                        )}
                                        isSold={item?.sold}
                                        chooseWorkshop={() => chooseWorkshop(item)}
                                    />
                                </Box>
                            ))}
                    </Box>
                </Card>
            </Box>
        </DashboardLayout>
    );
};

export default ChooseAdditionalWorkshop;
